import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  passkeysSupported, registerPasskey, listPasskeys, deletePasskey,
} from "@/lib/webauthn";
import { formatDate } from "@/lib/helpers";

export default function PasskeysModal({ onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const rows = await listPasskeys();
      setList(rows);
    } catch (e) {
      toast.error("Error cargando passkeys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doRegister = async () => {
    if (!passkeysSupported()) {
      toast.error("Este navegador no soporta passkeys");
      return;
    }
    setBusy(true);
    try {
      const suggested = label.trim()
        || (/(iphone|ipad|ios)/i.test(navigator.userAgent) ? "iPhone/iPad (Face ID)"
        : /android/i.test(navigator.userAgent) ? "Móvil Android (huella)"
        : /mac/i.test(navigator.userAgent) ? "Mac (Touch ID)"
        : /windows/i.test(navigator.userAgent) ? "PC Windows (Hello)"
        : "Este dispositivo");
      await registerPasskey(suggested);
      toast.success("Passkey registrada. La próxima vez podrás entrar con huella / Face ID.");
      setLabel("");
      await load();
    } catch (e) {
      if (e?.name === "NotAllowedError" || e?.message?.includes("cancel")) {
        toast.error("Registro cancelado");
      } else {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : (e.message || "Error al registrar passkey"));
      }
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`¿Eliminar la passkey "${row.label}"? No podrás iniciar sesión con este dispositivo hasta que la registres de nuevo.`)) return;
    try {
      await deletePasskey(row.id);
      toast.success("Passkey eliminada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal p-6" style={{ maxWidth: 560 }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Huella / Face ID · Passkeys</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl">✕</button>
        </div>

        {!passkeysSupported() ? (
          <div className="card p-4 bg-amber-50 border-amber-200 text-sm text-amber-900">
            Este navegador o dispositivo no soporta passkeys. Prueba en un móvil con
            huella/Face ID o en un ordenador con Touch ID / Windows Hello.
          </div>
        ) : (
          <>
            <div className="card p-4 mb-4"
                 style={{ background: "linear-gradient(135deg,#fff7ed 0%,#eff6ff 100%)" }}>
              <div className="font-semibold text-sm mb-2">Registrar este dispositivo</div>
              <p className="text-xs text-slate-600 mb-3">
                Podrás iniciar sesión con tu huella dactilar, Face ID, Touch ID o
                Windows Hello desde este mismo dispositivo. La biometría no sale de tu
                dispositivo — el servidor solo guarda una clave pública.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  className="input flex-1"
                  placeholder="Etiqueta (ej. iPhone de Juan)"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  data-testid="passkey-label"
                />
                <button
                  className="btn btn-primary"
                  onClick={doRegister}
                  disabled={busy}
                  data-testid="passkey-register"
                >
                  {busy ? "Registrando..." : "Registrar huella / Face ID"}
                </button>
              </div>
            </div>

            <div className="font-semibold text-sm mb-2">Dispositivos registrados</div>
            {loading ? (
              <div className="text-sm text-slate-400 py-6 text-center">Cargando...</div>
            ) : list.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">
                Aún no hay ningún dispositivo registrado.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                {list.map((row) => (
                  <div key={row.id}
                       className="flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-b-0">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{row.label || "Dispositivo"}</div>
                      <div className="text-[11px] text-slate-500">
                        Alta: {formatDate(row.created_at)}
                        {row.last_used_at && (
                          <> · Último uso: {formatDate(row.last_used_at)}</>
                        )}
                      </div>
                    </div>
                    <button
                      className="btn btn-danger !py-1 !px-3"
                      onClick={() => doDelete(row)}
                      data-testid={`passkey-delete-${row.id}`}
                    >
                      Eliminar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end mt-5">
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
